import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  RiAddLargeLine,
  RiFolderImageLine,
  RiPencilLine,
  RiQuillPenAiLine,
} from "@remixicon/react";
import { LuChevronDown, LuSearch } from "react-icons/lu";
import type { Moodboard } from "../../types";

interface Props {
  value: string | null | undefined;
  moodboards: Moodboard[];
  loading?: boolean;
  onChange: (value: string | null) => void;
  onCreateMoodboard?: () => void;
  onEditMoodboard?: (moodboardId: string) => void;
}

const MoodboardDropdown: React.FC<Props> = ({
  value,
  moodboards,
  loading = false,
  onChange,
  onCreateMoodboard,
  onEditMoodboard,
}) => {
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => (value ? moodboards.find((m) => m.id === value) : undefined),
    [moodboards, value],
  );
  const isOptimisticValue = !!value && value.startsWith("optimistic-");
  const isLoadingSelected = !!value && !selected && loading;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return moodboards;
    return moodboards.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [moodboards, query]);

  const selectedThumb = selected?.files?.[0]?.public_url;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-[6px] border-[1px] h-8 px-3 rounded-[8px] bg-white"
        >
          <div className="flex items-center gap-[6px] text-brand-primary-800">
            <RiQuillPenAiLine size={16} className="text-brand-primary-400 shrink-0" />
            <span className="text-[12px] tracking-[0.24px]">Style</span>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            {selected ? (
              <>
                {selectedThumb ? (
                  <img
                    src={selectedThumb}
                    alt={selected.name}
                    className="h-6 w-6 rounded-[8px] object-cover shrink-0"
                  />
                ) : (
                  <div className="h-6 w-6 rounded-[8px] bg-[#F2F2EE] flex items-center justify-center shrink-0">
                    <RiFolderImageLine size={14} className="text-brand-primary-400" />
                  </div>
                )}
                <span className="text-[12px] leading-[20px] font-medium font-['Helvetica Neue'] text-[#35467A] truncate max-w-[104px] inline-block align-middle">
                  {selected.name}
                </span>
              </>
            ) : isLoadingSelected ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-6 w-6 rounded-[8px] bg-[#EDEDE7] animate-pulse shrink-0" />
                <div className="h-[12px] w-[64px] rounded-[6px] bg-[#EDEDE7] animate-pulse" />
              </div>
            ) : isOptimisticValue ? (
              <span className="text-[12px] leading-[20px] font-medium font-['Helvetica Neue'] text-[#35467A]">
                Creating…
              </span>
            ) : (
              <span className="text-[12px] text-brand-primary-400">None</span>
            )}
          </div>

          <LuChevronDown className="h-4 w-4 text-brand-primary-new shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className={cn(
          "bg-white w-[180px] rounded-[12px] border-[0.8px] border-[#E7E8E9] shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.1),0px_1px_3px_rgba(0,0,0,0.1)]",
          "p-0 py-[4px] flex flex-col items-center",
        )}
        // Prevent Radix DropdownMenu typeahead from stealing focus while typing in the search input
        onKeyDown={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.tagName === "INPUT" || target?.getAttribute("role") === "textbox") {
            e.stopPropagation();
          }
        }}
      >
        {/* Container (padding 0 4px, gap 4px) */}
        <div className="w-full flex flex-col items-center px-[4px] gap-[4px]">
          {/* Search row */}
          <div className="w-full h-[28px] px-[8px] pt-[8px] pb-[6px] bg-[#F6F6F6] rounded-[8px] flex items-center">
            <div className="flex items-center gap-[6px]">
              <LuSearch className="h-[14px] w-[14px] text-[#9AA3BD] shrink-0" />
              <span className="sr-only">Search</span>
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              className="w-full bg-transparent outline-none text-[12px] leading-[20px] font-normal font-['Helvetica Neue'] text-[#35467A] placeholder:text-[#9AA3BD] pl-[6px]"
            />
          </div>

          {/* Options list */}
          <div className="w-full flex flex-col items-center">
            {loading ? (
              <div className="w-[172px] px-[8px] py-[4px] text-[12px] leading-[20px] text-[#9AA3BD]">
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="w-[172px] px-[8px] py-[4px] text-[12px] leading-[20px] text-[#9AA3BD]">
                No moodboards found
              </div>
            ) : (
              <div className="w-full max-h-[168px] overflow-y-auto overflow-x-hidden">
                <div className="w-full flex flex-col items-center gap-[4px]">
                  {/* None (scrolls with list) */}
                  <DropdownMenuItem
                    className={cn(
                      "w-[172px] h-[28px] px-[8px] py-[4px] rounded-[6px] flex items-center justify-between cursor-pointer",
                      !value ? "bg-[#F6F6F6]" : "bg-white hover:bg-[#F6F6F6]",
                    )}
                    onClick={() => onChange(null)}
                  >
                    <span className="text-[12px] leading-[20px] font-normal font-['Helvetica Neue'] text-[#35467A]">
                      None
                    </span>
                  </DropdownMenuItem>

                  {filtered.map((m) => {
                    const thumb = m.files?.[0]?.public_url;
                    const active = value === m.id;
                    return (
                      <DropdownMenuItem
                        key={m.id}
                        className={cn(
                          "group w-[172px] h-[28px] px-[8px] py-[4px] rounded-[4px] flex items-center justify-between cursor-pointer",
                          active ? "bg-[#F6F6F6]" : "bg-white hover:bg-[#F6F6F6]",
                        )}
                        onClick={() => onChange(m.id)}
                      >
                        <div className="flex items-center gap-[6px] min-w-0">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={m.name}
                              className="h-[20px] w-[20px] rounded-[6px] object-cover shrink-0"
                            />
                          ) : (
                            <div className="h-[20px] w-[20px] rounded-[6px] bg-[#F6F6F6] flex items-center justify-center shrink-0">
                              <RiFolderImageLine size={14} className="text-[#9AA3BD]" />
                            </div>
                          )}
                          <span className="truncate text-[12px] leading-[20px] font-normal font-['Helvetica Neue'] text-[#35467A]">
                            {m.name}
                          </span>
                        </div>

                        {/* Pencil icon (visual only for now) */}
                        <button
                          type="button"
                          className={cn(
                            "shrink-0 opacity-0 transition-opacity",
                            "group-hover:opacity-100 group-focus-within:opacity-100",
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onEditMoodboard?.(m.id);
                          }}
                          aria-label="Edit moodboard"
                        >
                          <RiPencilLine size={14} className="text-[#9AA3BD]" />
                        </button>
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No divider in Figma; keep same gap as list */}
            <div aria-hidden="true" className="h-[4px] w-full shrink-0" />

            <DropdownMenuItem
              className="w-[172px] h-[28px] px-[8px] py-[4px] rounded-[4px] flex items-center gap-[6px] cursor-pointer bg-white hover:bg-[#F6F6F6]"
              onClick={() => onCreateMoodboard?.()}
            >
              <RiAddLargeLine size={14} className="shrink-0 text-[#FF4500]" />
              <span className="text-[12px] leading-[20px] font-normal tracking-[0px] font-['Helvetica Neue'] text-[#FF4500]">
                Create Moodboard
              </span>
            </DropdownMenuItem>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MoodboardDropdown;
